/**
 * Catálogos fijos del módulo e-CF.
 *
 * Es lo que quedó de `mockData.ts` cuando las cuatro pantallas pasaron a leer del backend: aquí
 * ya no hay datos de ejemplo, solo las tablas que fija la DGII y que no viven en la base porque
 * no cambian con la empresa — los diez tipos de comprobante, los tres ambientes y los quince
 * pasos del portal de certificación.
 *
 * Los datos de la operación (emitidos, recibidos, resumen del panel, traza de los webservices)
 * los sirve `src/services/ecf.service.ts`. Ninguna pantalla debe inventarse los suyos.
 */

// ─── Catálogos de la DGII ────────────────────────────────────────────────────

/** Los 10 tipos de e-CF que reconoce la DGII. */
export const DOC_TYPE_LABEL: Record<string, string> = {
  '31': 'Crédito Fiscal',
  '32': 'Consumo',
  '33': 'Nota de Débito',
  '34': 'Nota de Crédito',
  '41': 'Compras',
  '43': 'Gastos Menores',
  '44': 'Régimen Especial',
  '45': 'Gubernamental',
  '46': 'Exportaciones',
  '47': 'Pagos al Exterior',
};

export const ENVIRONMENTS = [
  { value: 'test', label: 'TesteCF (desarrollo)' },
  { value: 'cert', label: 'CerteCF (certificación)' },
  { value: 'prod', label: 'eCF (producción)' },
] as const;

export type EcfEnvironment = (typeof ENVIRONMENTS)[number]['value'];

// ─── Asistente de certificación (15 pasos del portal DGII) ───────────────────

/** Quién tiene que actuar en cada paso. Determina el color y el texto del botón. */
export type StepActor = 'app' | 'portal' | 'dgii' | 'auto';
export type StepPhase = 'Preparación' | 'Emisión' | 'Recepción' | 'Producción';

export interface StepDef {
  id: number;
  label: string;
  desc: string;
  actor: StepActor;
  phase: StepPhase;
}

export const STEP_DEFS: StepDef[] = [
  {
    id: 1,
    label: 'Registrado en el portal',
    desc: 'La empresa ya solicitó la certificación en el portal de la DGII y tiene acceso al asistente.',
    actor: 'portal',
    phase: 'Preparación',
  },
  {
    id: 2,
    label: 'Pruebas de datos e-CF',
    desc: 'Se cargan los sets de prueba que entrega la DGII, se firman y se envían desde aquí.',
    actor: 'app',
    phase: 'Emisión',
  },
  {
    id: 3,
    label: 'Pruebas de aprobación comercial',
    desc: 'Se envían las aprobaciones comerciales del set de prueba.',
    actor: 'app',
    phase: 'Emisión',
  },
  {
    id: 4,
    label: 'Simulación con tus datos',
    desc: 'Mismo set, pero con el RNC y los datos reales de la empresa.',
    actor: 'app',
    phase: 'Emisión',
  },
  {
    id: 5,
    label: 'Representación impresa',
    desc: 'Se generan los PDF de cada tipo de comprobante para enviarlos a la DGII.',
    actor: 'app',
    phase: 'Emisión',
  },
  {
    id: 6,
    label: 'Validación de la RI',
    desc: 'La DGII revisa que las representaciones impresas cumplan el formato oficial.',
    actor: 'dgii',
    phase: 'Emisión',
  },
  {
    id: 7,
    label: 'URL de servicios de prueba',
    desc: 'Se registran en el portal las tres URLs por las que la DGII te enviará comprobantes.',
    actor: 'portal',
    phase: 'Recepción',
  },
  {
    id: 8,
    label: 'Inicio de recepción',
    desc: 'La DGII llama a tus webservices. El sistema responde solo, sin que nadie intervenga.',
    actor: 'auto',
    phase: 'Recepción',
  },
  {
    id: 9,
    label: 'Recepción de e-CF',
    desc: 'La DGII verifica que recibiste y acusaste correctamente los comprobantes.',
    actor: 'dgii',
    phase: 'Recepción',
  },
  {
    id: 10,
    label: 'Inicio de aprobación comercial',
    desc: 'La DGII envía aprobaciones comerciales a tu webservice. Se responden automáticamente.',
    actor: 'auto',
    phase: 'Recepción',
  },
  {
    id: 11,
    label: 'Aprobación comercial',
    desc: 'La DGII valida las respuestas de aprobación comercial.',
    actor: 'dgii',
    phase: 'Recepción',
  },
  {
    id: 12,
    label: 'URL de servicios de producción',
    desc: 'Se registran las URLs definitivas, ya con el ambiente de producción.',
    actor: 'portal',
    phase: 'Producción',
  },
  {
    id: 13,
    label: 'Declaración jurada',
    desc: 'Se firma en el portal la declaración jurada de cumplimiento.',
    actor: 'portal',
    phase: 'Producción',
  },
  {
    id: 14,
    label: 'Verificación de estatus',
    desc: 'La DGII emite la resolución que autoriza a emitir en producción.',
    actor: 'dgii',
    phase: 'Producción',
  },
  {
    id: 15,
    label: 'Finalizado',
    desc: 'La empresa ya puede emitir comprobantes fiscales electrónicos en producción.',
    actor: 'app',
    phase: 'Producción',
  },
];

/** Qué significa cada actor en la UI: color del bloque y texto del botón de avance. */
export const ACTOR_UI: Record<
  StepActor,
  { titulo: string; icon: string; box: string; dot: string; cta: string }
> = {
  app: {
    titulo: 'Lo haces aquí',
    icon: 'ri-plug-line',
    box: 'border-blue-200 bg-blue-50',
    dot: 'bg-blue-600',
    cta: 'Marcar completado y continuar',
  },
  portal: {
    titulo: 'Lo haces en el portal de la DGII',
    icon: 'ri-external-link-line',
    box: 'border-amber-200 bg-amber-50',
    dot: 'bg-amber-500',
    cta: 'Ya lo hice en el portal',
  },
  dgii: {
    titulo: 'La DGII lo está revisando',
    icon: 'ri-time-line',
    box: 'border-gray-200 bg-gray-50',
    dot: 'bg-gray-400',
    cta: 'La DGII ya lo aprobó',
  },
  auto: {
    titulo: 'Tu sistema responde solo',
    icon: 'ri-inbox-line',
    box: 'border-indigo-200 bg-indigo-50',
    dot: 'bg-indigo-600',
    cta: 'Marcar completado y continuar',
  },
};

export const PORTAL_DGII_URL = 'https://ecf.dgii.gov.do/certecf/portalcertificacion';
